/**
 * Named point-set persistence — save and load boundary-point sets by name,
 * optionally scoped to a project ID.
 *
 * Storage keys:
 *   girasoles_pointsets                      – index of all saved sets
 *   girasoles_pointset_<id>                  – the actual point array
 */

import { isObject, readValidatedJSON, type Validator } from './safeStorage'

export interface SavedPointSet {
  id: string
  name: string
  projectId?: string
  pointCount: number
  createdAt: string
  updatedAt: string
}

export interface PointSetData {
  id: string
  position: { x: number; y: number; z: number }
  timestamp: number
  type?: 'point' | 'measure'
}

const INDEX_KEY = 'girasoles_pointsets'
const DATA_PREFIX = 'girasoles_pointset_'

const isSavedPointSet: Validator<SavedPointSet> = ((v: unknown): v is SavedPointSet =>
  isObject(v) &&
  typeof v.id === 'string' &&
  typeof v.name === 'string' &&
  (v.projectId === undefined || typeof v.projectId === 'string') &&
  typeof v.pointCount === 'number' &&
  typeof v.createdAt === 'string' &&
  typeof v.updatedAt === 'string') as Validator<SavedPointSet>

const isVec3 = (v: unknown): v is { x: number; y: number; z: number } =>
  isObject(v) && typeof v.x === 'number' && typeof v.y === 'number' && typeof v.z === 'number'

const isPointSetData: Validator<PointSetData> = ((v: unknown): v is PointSetData =>
  isObject(v) &&
  typeof v.id === 'string' &&
  isVec3(v.position) &&
  typeof v.timestamp === 'number' &&
  (v.type === undefined || v.type === 'point' || v.type === 'measure')) as Validator<PointSetData>

const isSavedPointSetArray: Validator<SavedPointSet[]> = ((v: unknown): v is SavedPointSet[] =>
  Array.isArray(v) && v.every(isSavedPointSet)) as Validator<SavedPointSet[]>

const isPointSetDataArray: Validator<PointSetData[]> = ((v: unknown): v is PointSetData[] =>
  Array.isArray(v) && v.every(isPointSetData)) as Validator<PointSetData[]>

function generateId(): string {
  return `ps_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

function loadIndex(): SavedPointSet[] {
  return readValidatedJSON(INDEX_KEY, isSavedPointSetArray, []) ?? []
}

function saveIndex(index: SavedPointSet[]): void {
  localStorage.setItem(INDEX_KEY, JSON.stringify(index))
}

/**
 * Save a named point set. If a set with the same name (and optionally same projectId)
 * already exists, it is overwritten.
 */
export function savePointSet(
  name: string,
  points: PointSetData[],
  projectId?: string
): SavedPointSet {
  const index = loadIndex()
  const now = new Date().toISOString()

  // Check for existing set with same name + projectId
  const existingIdx = index.findIndex(
    s => s.name === name && s.projectId === projectId
  )

  let entry: SavedPointSet
  if (existingIdx >= 0) {
    entry = {
      ...index[existingIdx],
      pointCount: points.length,
      updatedAt: now,
    }
    index[existingIdx] = entry
  } else {
    entry = {
      id: generateId(),
      name,
      projectId,
      pointCount: points.length,
      createdAt: now,
      updatedAt: now,
    }
    index.push(entry)
  }

  saveIndex(index)
  localStorage.setItem(DATA_PREFIX + entry.id, JSON.stringify(points))
  return entry
}

/**
 * Load a named point set.
 */
export function loadPointSet(
  name: string,
  projectId?: string
): PointSetData[] | null {
  const index = loadIndex()
  const entry = index.find(
    s => s.name === name && (projectId === undefined || s.projectId === projectId)
  )
  if (!entry) return null

  return readValidatedJSON(DATA_PREFIX + entry.id, isPointSetDataArray)
}

/**
 * List all saved point sets, optionally filtered by projectId.
 */
export function listPointSets(projectId?: string): SavedPointSet[] {
  const index = loadIndex()
  if (projectId === undefined) return index
  return index.filter(s => s.projectId === projectId)
}

/**
 * Delete a named point set.
 */
export function deletePointSet(name: string, projectId?: string): boolean {
  const index = loadIndex()
  const idx = index.findIndex(
    s => s.name === name && (projectId === undefined || s.projectId === projectId)
  )
  if (idx < 0) return false

  const entry = index[idx]
  localStorage.removeItem(DATA_PREFIX + entry.id)
  index.splice(idx, 1)
  saveIndex(index)
  return true
}
